<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { getHelpUrlForFeature } from 'dashboard/helper/featureHelper';
import TalkviaOpenAIConfigAPI from 'dashboard/api/talkviaOpenAIConfig';
import TalkviaOpenAIPromptsAPI from 'dashboard/api/talkviaOpenAIPrompts';
import { filterPromptsBySearch } from './promptSearch';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const router = useRouter();
const { accountScopedRoute } = useAccount();

const copy = {
  title: 'Biblioteca de IA',
  eyebrow: 'Inteligência artificial',
  subtitle:
    'Centralize os comportamentos da IA e mantenha o atendimento consistente em toda a equipe.',
  tutorial: 'Como criar sua primeira Agente de IA',
  prompts: {
    title: 'Prompts prontos',
    description:
      'Crie instruções simples para a equipe usar sem precisar escrever comando técnico.',
    new: 'Novo prompt',
    save: 'Salvar prompt',
    cancel: 'Cancelar',
    edit: 'Editar',
    remove: 'Excluir',
    activate: 'Ativar',
    deactivate: 'Desativar',
    readMore: 'Ler mais',
    systemBadge: 'Padrão Talkvia',
    systemHint:
      'Mantido pela Talkvia e usado pelo Assistente de IA. Para outras instruções, escolha “Prompt personalizado” no assistente.',
    detailsTitle: 'Detalhes do prompt',
    close: 'Fechar',
    empty: 'Nenhum prompt cadastrado ainda.',
    searchPlaceholder: 'Buscar por nome, modelo ou conteúdo',
    searchLabel: 'Buscar prompts',
    searchEmpty: 'Nenhum prompt encontrado para esta busca.',
    activateTitle: 'Ativar prompt?',
    deactivateTitle: 'Desativar prompt?',
    activateDescription:
      'Ao ativar, este prompt voltará a ficar disponível para uso pela IA.',
    deactivateDescription:
      'Ao desativar, este prompt deixará de ser enviado para a IA até ser ativado novamente.',
    activateConfirm: 'Ativar prompt',
    deactivateConfirm: 'Desativar prompt',
    deleteTitle: 'Excluir prompt?',
    deleteDescription:
      'Esta ação remove o prompt da biblioteca e não pode ser desfeita.',
    deleteConfirm: 'Excluir prompt',
  },
  templates: {
    title: 'Modelos prontos',
    description:
      'Use um ponto de partida e ajuste o texto antes de salvar como prompt da conta.',
    open: 'Escolher modelo',
    use: 'Usar modelo',
    search: 'Buscar modelo',
    allCategories: 'Todos',
    empty: 'Nenhum modelo encontrado.',
    close: 'Fechar',
  },
  fields: {
    name: 'Nome do prompt',
    category: 'Tipo de uso',
    model: 'Modelo',
    instructions: 'O que a IA deve fazer',
    context: 'Informações que a IA deve considerar',
    aiName: 'Nome da IA',
    variables: 'Variáveis dinâmicas',
    insertVariable: 'Inserir variável',
    variablesDescription:
      'Posicione o cursor nas instruções e escolha uma variável. O valor será preenchido automaticamente em cada conversa.',
    authorizationNotice: 'Aviso de autorização por CPF',
    active: 'Prompt ativo',
    activeDescription: 'Prompt ativo e disponível para uso',
    inactiveDescription: 'Prompt desativado',
  },
  knowledgeBase: {
    title: 'Base de conhecimento',
    description:
      'Anexe arquivos Markdown para a IA consultar. O conteúdo é baixado somente quando necessário e não segue em cada webhook.',
    add: 'Anexar arquivos .md',
    replace: 'Substituir',
    remove: 'Remover',
    empty: 'Nenhum arquivo Markdown anexado.',
    pending: 'Será enviado ao salvar o prompt.',
    version: 'Versão',
    updatedAt: 'Atualizado em',
    uploadFailed: 'Não foi possível enviar um ou mais arquivos Markdown.',
    removeFailed: 'Não foi possível remover o arquivo.',
    invalid: 'Selecione somente arquivos com extensão .md.',
    separator: '·',
    statuses: {
      processing: 'Processando',
      ready: 'Pronto',
      failed: 'Falhou',
    },
  },
  builder: {
    eyebrow: 'Construtor de comportamento',
    description:
      'Defina a identidade, as instruções e os limites da IA. Você acompanha o resultado enquanto preenche.',
    identityTitle: 'Identidade e finalidade',
    identityDescription:
      'Dê um nome fácil de encontrar e defina como a IA deve se apresentar.',
    behaviorTitle: 'Comportamento principal',
    behaviorDescription:
      'Explique a tarefa, o tom de voz e o resultado esperado. Seja direto e use as variáveis quando precisar personalizar.',
    contextTitle: 'Contexto e limites',
    contextDescription:
      'Adicione regras do negócio, informações obrigatórias e situações que exigem cuidado.',
    stepOne: '1',
    stepTwo: '2',
    stepThree: '3',
    previewTitle: 'Resumo da configuração',
    previewDescription:
      'Confira os principais dados sem exibir o conteúdo completo das instruções.',
    checklistTitle: 'Pronto para salvar',
    nameReady: 'Nome do prompt',
    identityReady: 'Identidade da IA',
    instructionsReady: 'Instrução principal',
    contextReady: 'Contexto adicional',
    optional: 'Opcional',
    promptName: 'Prompt',
    instructionSize: 'Instruções',
    variablesUsed: 'Variáveis utilizadas',
    noVariables: 'Nenhuma variável inserida',
  },
  placeholders: {
    name: 'Ex.: Responder cliente interessado',
    aiName: 'Ex.: Pam, Deisy ou Aurora',
    instructions:
      'Ex.: Responda de forma clara, educada e objetiva. Sempre confirme o próximo passo com o cliente.',
    context:
      'Ex.: Use linguagem simples. Quando falar de valores, peça confirmação antes de prometer aprovação.',
    authorizationNotice:
      'Escreva o aviso que será enviado antes do cliente informar o CPF.',
  },
  authorizationNotice: {
    title: 'Autorização para consulta por CPF',
    description:
      'Disponível porque esta conta possui Corban ativo. O texto será enviado exatamente como estiver aqui antes do aceite.',
    urlNotice:
      'O link individual dos termos é acrescentado automaticamente ao final da mensagem.',
    emptyNotice: 'Se ficar vazio, o sistema usará o aviso jurídico padrão.',
  },
  messages: {
    loading: 'Carregando modelos prontos...',
    loadFailed: 'Não foi possível carregar os modelos prontos.',
    savedPrompt: 'Prompt salvo.',
    removedPrompt: 'Prompt excluído.',
    activatedPrompt: 'Prompt ativado.',
    deactivatedPrompt: 'Prompt desativado.',
    saveFailed: 'Não foi possível salvar.',
  },
  summary: {
    title: 'Resumo',
    activePrompts: 'Prompts ativos',
    models: 'Modelo padrão',
    description:
      'Use modelos prontos para padronizar mensagens e facilitar a criação de prompts grandes.',
  },
  notConfigured: {
    title: 'OpenAI não configurada',
    description:
      'Para usar a biblioteca de IA, prompts e sugestões automáticas, você precisa conectar sua chave de API da OpenAI na página de integrações.',
    stepsTitle: 'Como configurar em 3 passos:',
    steps: [
      {
        num: '1',
        title: 'Acesse Configurações → Integrações',
        desc: 'No menu lateral, vá em Configurações e abra a aba Integrações.',
      },
      {
        num: '2',
        title: 'Localize a integração OpenAI e clique em Configurar',
        desc: 'A OpenAI aparece na seção de integrações disponíveis.',
      },
      {
        num: '3',
        title: 'Insira sua chave de API e salve',
        desc: 'Crie sua chave em platform.openai.com/api-keys e cole aqui.',
      },
    ],
    button: 'Ir para integrações → OpenAI',
  },
  form: {
    editPrompt: 'Editar prompt',
    fixedModel: 'ChatGPT 5.4-mini',
    noModel: 'Nenhum modelo pronto',
    active: 'Ativo',
    inactive: 'Inativo',
  },
};

const DEFAULT_TEMPLATE_CATEGORY = 'geral';
const DEFAULT_AUTHORIZATION_NOTICE =
  'Para consultar as condições de crédito disponíveis, preciso da sua autorização para consultar os dados vinculados ao seu CPF nas instituições participantes. Seus dados serão utilizados exclusivamente para essa finalidade. Leia os termos no link abaixo e, se estiver de acordo, responda a esta mensagem informando apenas o seu CPF.';

const inferTemplateCategory = modelName => {
  const normalized = (modelName || '').toLowerCase();
  if (normalized.includes('venda')) return 'vendas';
  if (normalized.includes('cobran')) return 'cobranca';
  if (normalized.includes('suporte')) return 'suporte';
  if (normalized.includes('anal')) return 'analise';
  if (normalized.includes('atendimento')) return 'atendimento';

  return DEFAULT_TEMPLATE_CATEGORY;
};

const categoryLabelFor = (category, categories) => {
  if (categories[category]) return categories[category];
  if (category === DEFAULT_TEMPLATE_CATEGORY) return 'Geral';
  return category;
};

const buildTemplateInstructions = model => {
  return (
    model.prompt_template ||
    model.developer_message ||
    model.description ||
    `Use o modelo ${model.name} (${model.model_id}) para responder com clareza e objetividade.`
  );
};

const isLoading = ref(false);
const isSavingPrompt = ref(false);
const instructionsTextareaRef = ref(null);
const togglingPromptId = ref(null);
const isDeletingPrompt = ref(false);
const knowledgeFileInput = ref(null);
const knowledgeBaseFiles = ref([]);
const pendingKnowledgeFiles = ref([]);
const replacingKnowledgeFileId = ref(null);
const uploadingKnowledgeFiles = ref(false);
const deletingKnowledgeFileId = ref(null);
const editingPromptId = ref(null);
const showPromptModal = ref(false);
const showTemplateModal = ref(false);
const selectedPrompt = ref(null);
const promptToDelete = ref(null);
const promptToToggle = ref(null);
const promptSearch = ref('');
const templateSearch = ref('');
const selectedTemplateCategory = ref('all');
const models = ref([]);
const prompts = ref([]);
const templates = ref([]);
const categories = ref({});
const setting = ref({});
const features = ref({});

const promptForm = reactive({
  name: '',
  ai_name: '',
  category: 'atendimento',
  talkvia_openai_model_id: '',
  instructions: '',
  user_context: '',
  consultation_authorization_notice_text: DEFAULT_AUTHORIZATION_NOTICE,
  active: true,
});

const promptVariables = [
  { key: 'nome_ia', label: 'Nome da IA' },
  { key: 'primeiro_nome_cliente', label: 'Primeiro nome do cliente' },
  { key: 'nome_cliente', label: 'Nome completo do cliente' },
  { key: 'email_cliente', label: 'E-mail do cliente' },
  { key: 'telefone_cliente', label: 'Telefone do cliente' },
  { key: 'codigo_conversa', label: 'Código da conversa' },
  { key: 'nome_caixa', label: 'Nome da caixa' },
  { key: 'nome_conta', label: 'Nome da conta' },
  { key: 'nome_agente', label: 'Agente responsável' },
  { key: 'data_atual', label: 'Data atual' },
  { key: 'hora_atual', label: 'Hora atual' },
];

const insertPromptVariable = variable => {
  const token = `{{${variable}}}`;
  if (instructionsTextareaRef.value) {
    instructionsTextareaRef.value.insertTextAtCursor(token);
    return;
  }
  promptForm.instructions = `${promptForm.instructions}${token}`;
};

const promptVariableToken = variable => `{{${variable}}}`;

const promptVariablesUsed = computed(() => {
  const matches = promptForm.instructions.matchAll(/\{\{\s*([a-z_]+)\s*\}\}/g);
  return [...new Set(Array.from(matches, match => match[1]))].filter(variable =>
    promptVariables.some(item => item.key === variable)
  );
});

const promptInstructionSize = computed(() => {
  const size = promptForm.instructions.trim().length;
  return size === 1 ? '1 caractere' : `${size} caracteres`;
});

const promptCompletionIconClass = item => {
  if (item.complete) return 'text-n-teal-10';
  if (item.optional) return 'text-n-slate-8';
  return 'text-n-slate-9';
};

const promptCompletionItems = computed(() =>
  [
    {
      label: copy.builder.nameReady,
      complete: Boolean(promptForm.name.trim()),
      optional: false,
    },
    {
      label: copy.builder.identityReady,
      complete: Boolean(promptForm.ai_name.trim()),
      optional: true,
    },
    {
      label: copy.builder.instructionsReady,
      complete: Boolean(promptForm.instructions.trim()),
      optional: false,
    },
    {
      label: copy.builder.contextReady,
      complete: Boolean(promptForm.user_context.trim()),
      optional: true,
    },
  ].map(item => ({
    ...item,
    icon:
      item.complete || item.optional
        ? 'i-lucide-circle-check'
        : 'i-lucide-circle',
    iconClass: promptCompletionIconClass(item),
  }))
);

const promptCompletion = computed(() => {
  const requiredItems = promptCompletionItems.value.filter(
    item => !item.optional
  );
  const completed = requiredItems.filter(item => item.complete).length;
  return Math.round((completed / requiredItems.length) * 100);
});

const promptCompletionLabel = computed(() => `${promptCompletion.value}%`);

const selectedCategoryLabel = computed(
  () => categories.value[promptForm.category] || promptForm.category
);

const corbanActive = computed(() => Boolean(features.value.corban_active));

const fixedPromptModel = computed(
  () =>
    models.value.find(model => model.model_id === 'gpt-6-luna') ||
    models.value[0] ||
    null
);

const fixedPromptModelId = computed(() => fixedPromptModel.value?.id || '');

const openAiAgentTutorial = () => {
  window.open(getHelpUrlForFeature('ai_agent'), '_blank', 'noopener');
};

const fixedPromptModelLabel = computed(() =>
  fixedPromptModel.value
    ? `${copy.form.fixedModel} (${fixedPromptModel.value.model_id})`
    : copy.form.noModel
);

const categoryOptions = computed(() =>
  Object.entries(categories.value).map(([value, label]) => ({ value, label }))
);

const activePrompts = computed(() =>
  prompts.value.filter(prompt => prompt.active)
);

const filteredPrompts = computed(() =>
  filterPromptsBySearch(prompts.value, promptSearch.value)
);

const hasOpenAI = computed(() => Boolean(setting.value.api_key_configured));

const goToOpenAIIntegration = () => {
  router.push(
    accountScopedRoute('settings_applications_integration', {
      integration_id: 'openai',
    })
  );
};

const templateCategoryOptions = computed(() => {
  const availableCategories = new Set(
    templates.value.map(template => template.category)
  );

  const dynamicCategories = Array.from(availableCategories).map(value => ({
    value,
    label: categoryLabelFor(value, categories.value),
  }));

  return [
    { value: 'all', label: copy.templates.allCategories },
    ...dynamicCategories,
  ];
});

const filteredTemplates = computed(() => {
  const searchTerm = templateSearch.value.trim().toLowerCase();
  return templates.value.filter(template => {
    const matchesCategory =
      selectedTemplateCategory.value === 'all' ||
      template.category === selectedTemplateCategory.value;
    const matchesSearch =
      !searchTerm ||
      [template.name, template.description, template.instructions]
        .join(' ')
        .toLowerCase()
        .includes(searchTerm);

    return matchesCategory && matchesSearch;
  });
});

const promptPreview = prompt => {
  const text = (prompt.instructions || '').replace(/\s+/g, ' ').trim();
  if (text.length <= 260) return text;
  return `${text.slice(0, 260)}...`;
};

const hasPromptDetails = prompt =>
  Boolean(prompt.instructions || prompt.user_context);

const openPromptDetails = prompt => {
  selectedPrompt.value = prompt;
};

const closePromptDetails = () => {
  selectedPrompt.value = null;
};

const resetPromptForm = () => {
  editingPromptId.value = null;
  knowledgeBaseFiles.value = [];
  pendingKnowledgeFiles.value = [];
  replacingKnowledgeFileId.value = null;
  Object.assign(promptForm, {
    name: '',
    ai_name: '',
    category: 'atendimento',
    talkvia_openai_model_id: fixedPromptModelId.value,
    instructions: '',
    user_context: '',
    consultation_authorization_notice_text: DEFAULT_AUTHORIZATION_NOTICE,
    active: true,
  });
};

const openNewPromptModal = () => {
  resetPromptForm();
  showPromptModal.value = true;
};

const closePromptModal = () => {
  showPromptModal.value = false;
  resetPromptForm();
};

const fillConfig = data => {
  setting.value = data.setting || {};
  models.value = data.models || [];
  prompts.value = data.prompts || [];
  categories.value = data.categories || {};
  features.value = data.features || {};
  templates.value = models.value.map(model => ({
    id: `model-${model.id}`,
    name: model.name,
    category: inferTemplateCategory(model.name),
    description:
      model.description ||
      `Baseado no modelo ${model.model_id} configurado no super admin.`,
    instructions: buildTemplateInstructions(model),
  }));

  const preferredModelId =
    fixedPromptModelId.value ||
    setting.value.talkvia_openai_model_id ||
    models.value[0]?.id ||
    '';

  if (!promptForm.talkvia_openai_model_id) {
    promptForm.talkvia_openai_model_id = preferredModelId;
  }
};

const fetchConfig = async () => {
  isLoading.value = true;
  try {
    const { data } = await TalkviaOpenAIConfigAPI.get();
    fillConfig(data);
  } catch (error) {
    useAlert(copy.messages.loadFailed);
  } finally {
    isLoading.value = false;
  }
};

const editPrompt = prompt => {
  editingPromptId.value = prompt.id;
  knowledgeBaseFiles.value = [...(prompt.knowledge_base_files || [])];
  pendingKnowledgeFiles.value = [];
  Object.assign(promptForm, {
    name: prompt.name,
    ai_name: prompt.ai_name || prompt.settings?.ai_name || '',
    category: prompt.category,
    talkvia_openai_model_id: fixedPromptModelId.value,
    instructions: prompt.instructions,
    user_context: prompt.user_context || '',
    consultation_authorization_notice_text:
      prompt.settings?.consultation_authorization_notice_text || '',
    active: prompt.active,
  });
  showPromptModal.value = true;
};

const useTemplate = template => {
  editingPromptId.value = null;
  knowledgeBaseFiles.value = [];
  pendingKnowledgeFiles.value = [];
  Object.assign(promptForm, {
    ...template,
    ai_name: '',
    talkvia_openai_model_id: fixedPromptModelId.value,
    user_context: '',
    consultation_authorization_notice_text: DEFAULT_AUTHORIZATION_NOTICE,
    active: true,
  });
  showTemplateModal.value = false;
  showPromptModal.value = true;
};

const requestPromptToggle = prompt => {
  promptToToggle.value = prompt;
};

const closePromptToggle = () => {
  if (togglingPromptId.value) return;
  promptToToggle.value = null;
};

const confirmPromptToggle = async () => {
  const prompt = promptToToggle.value;
  if (!prompt) return;

  const wasActive = prompt.active;
  togglingPromptId.value = prompt.id;
  try {
    await TalkviaOpenAIPromptsAPI.update(prompt.id, {
      active: !wasActive,
    });
    await fetchConfig();
    promptToToggle.value = null;
    useAlert(
      wasActive
        ? copy.messages.deactivatedPrompt
        : copy.messages.activatedPrompt
    );
  } catch (error) {
    useAlert(copy.messages.saveFailed);
  } finally {
    togglingPromptId.value = null;
  }
};

const uploadKnowledgeFiles = async (promptId, files) => {
  uploadingKnowledgeFiles.value = true;
  try {
    const responses = await Promise.all(
      files.map(file =>
        TalkviaOpenAIPromptsAPI.uploadKnowledgeBaseFile(promptId, file)
      )
    );
    knowledgeBaseFiles.value.push(...responses.map(response => response.data));
    pendingKnowledgeFiles.value = [];
  } finally {
    uploadingKnowledgeFiles.value = false;
  }
};

const savePrompt = async () => {
  isSavingPrompt.value = true;
  let knowledgeUploadFailed = false;
  try {
    const payload = {
      ...promptForm,
      talkvia_openai_model_id: fixedPromptModelId.value || null,
    };
    if (!corbanActive.value) {
      delete payload.consultation_authorization_notice_text;
    }
    const response = editingPromptId.value
      ? await TalkviaOpenAIPromptsAPI.update(editingPromptId.value, payload)
      : await TalkviaOpenAIPromptsAPI.create(payload);
    const savedPromptId = editingPromptId.value || response.data.id;
    if (pendingKnowledgeFiles.value.length) {
      try {
        await uploadKnowledgeFiles(savedPromptId, pendingKnowledgeFiles.value);
      } catch (error) {
        knowledgeUploadFailed = true;
      }
    }
    await fetchConfig();
    resetPromptForm();
    showPromptModal.value = false;
    useAlert(
      knowledgeUploadFailed
        ? copy.knowledgeBase.uploadFailed
        : copy.messages.savedPrompt
    );
  } catch (error) {
    useAlert(copy.messages.saveFailed);
  } finally {
    isSavingPrompt.value = false;
  }
};

const isMarkdownFile = file =>
  file?.name?.toLowerCase().endsWith('.md') && file.size > 0;

const openKnowledgeFilePicker = fileId => {
  replacingKnowledgeFileId.value = fileId || null;
  knowledgeFileInput.value?.click();
};

const handleKnowledgeFileSelection = async event => {
  const selectedFiles = Array.from(event.target.files || []);
  event.target.value = '';
  if (!selectedFiles.length) return;
  if (selectedFiles.some(file => !isMarkdownFile(file))) {
    useAlert(copy.knowledgeBase.invalid);
    replacingKnowledgeFileId.value = null;
    return;
  }

  if (replacingKnowledgeFileId.value) {
    const fileId = replacingKnowledgeFileId.value;
    replacingKnowledgeFileId.value = null;
    uploadingKnowledgeFiles.value = true;
    try {
      const { data } = await TalkviaOpenAIPromptsAPI.replaceKnowledgeBaseFile(
        editingPromptId.value,
        fileId,
        selectedFiles[0]
      );
      knowledgeBaseFiles.value = knowledgeBaseFiles.value.map(file =>
        file.id === fileId ? data : file
      );
    } catch (error) {
      useAlert(copy.knowledgeBase.uploadFailed);
    } finally {
      uploadingKnowledgeFiles.value = false;
    }
    return;
  }

  if (!editingPromptId.value) {
    pendingKnowledgeFiles.value.push(...selectedFiles);
    return;
  }

  try {
    await uploadKnowledgeFiles(editingPromptId.value, selectedFiles);
  } catch (error) {
    useAlert(copy.knowledgeBase.uploadFailed);
  }
};

const removePendingKnowledgeFile = index => {
  pendingKnowledgeFiles.value.splice(index, 1);
};

const removeKnowledgeBaseFile = async file => {
  deletingKnowledgeFileId.value = file.id;
  try {
    await TalkviaOpenAIPromptsAPI.deleteKnowledgeBaseFile(
      editingPromptId.value,
      file.id
    );
    knowledgeBaseFiles.value = knowledgeBaseFiles.value.filter(
      currentFile => currentFile.id !== file.id
    );
  } catch (error) {
    useAlert(copy.knowledgeBase.removeFailed);
  } finally {
    deletingKnowledgeFileId.value = null;
  }
};

const formatFileSize = size => {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const formatUpdatedAt = date =>
  new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(date));

const knowledgeStatusLabel = status =>
  copy.knowledgeBase.statuses[status] || status;

const deletePrompt = prompt => {
  promptToDelete.value = prompt;
};

const confirmDeletePrompt = async () => {
  if (!promptToDelete.value) return;

  isDeletingPrompt.value = true;
  try {
    await TalkviaOpenAIPromptsAPI.delete(promptToDelete.value.id);
    await fetchConfig();
    promptToDelete.value = null;
    useAlert(copy.messages.removedPrompt);
  } catch (error) {
    useAlert(copy.messages.saveFailed);
  } finally {
    isDeletingPrompt.value = false;
  }
};

onMounted(fetchConfig);
</script>

<template>
  <main class="flex w-full min-w-0 flex-col gap-6 font-inter">
    <!-- Page header -->
    <header
      class="flex flex-col gap-5 rounded-3xl border border-n-weak bg-n-solid-1 p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:p-6"
    >
      <div class="flex min-w-0 items-start gap-4">
        <span
          class="grid size-12 shrink-0 place-items-center rounded-xl bg-n-violet-3 text-n-violet-11"
        >
          <Icon icon="i-lucide-library-big" class="size-6" />
        </span>
        <div class="min-w-0">
          <p
            class="mb-1 text-xs font-semibold uppercase tracking-wider text-n-violet-10"
          >
            {{ copy.eyebrow }}
          </p>
          <h1 class="text-2xl font-semibold text-n-slate-12">
            {{ copy.title }}
          </h1>
          <p class="mt-1 max-w-2xl text-sm leading-6 text-n-slate-11">
            {{ copy.subtitle }}
          </p>
        </div>
      </div>
      <Button
        :label="copy.tutorial"
        icon="i-lucide-book-open"
        color="slate"
        variant="faded"
        size="sm"
        @click="openAiAgentTutorial"
      />
    </header>

    <!-- Loading -->
    <div
      v-if="isLoading"
      class="flex items-center gap-3 rounded-xl border border-n-weak bg-n-solid-2 p-5 text-sm text-n-slate-11"
    >
      <Icon icon="i-lucide-loader-2" class="size-4 animate-spin" />
      {{ copy.messages.loading }}
    </div>

    <!-- OpenAI not configured -->
    <div
      v-else-if="!isLoading && !hasOpenAI"
      class="rounded-2xl border border-n-amber-6/50 bg-n-amber-2/30 p-6"
    >
      <div class="flex items-start gap-4">
        <div
          class="flex size-12 flex-shrink-0 items-center justify-center rounded-xl border border-n-amber-6 bg-n-amber-3"
        >
          <Icon icon="i-lucide-triangle-alert" class="size-6 text-n-amber-10" />
        </div>
        <div class="min-w-0 flex-1">
          <h2 class="text-base font-semibold text-n-slate-12">
            {{ copy.notConfigured.title }}
          </h2>
          <p class="mt-1 text-sm leading-6 text-n-slate-11">
            {{ copy.notConfigured.description }}
          </p>
        </div>
      </div>

      <div class="mt-6 rounded-xl border border-n-weak bg-n-solid-2 p-5">
        <p class="mb-4 text-sm font-semibold text-n-slate-12">
          {{ copy.notConfigured.stepsTitle }}
        </p>
        <ol class="space-y-4">
          <li
            v-for="step in copy.notConfigured.steps"
            :key="step.num"
            class="flex items-start gap-3"
          >
            <span
              class="flex size-6 flex-shrink-0 items-center justify-center rounded-full bg-n-brand/10 text-xs font-bold text-n-brand"
            >
              {{ step.num }}
            </span>
            <div class="min-w-0">
              <p class="text-sm font-medium text-n-slate-12">
                {{ step.title }}
              </p>
              <p class="mt-0.5 text-xs text-n-slate-10">{{ step.desc }}</p>
            </div>
          </li>
        </ol>
        <Button
          class="mt-5"
          :label="copy.notConfigured.button"
          icon="i-lucide-external-link"
          @click="goToOpenAIIntegration"
        />
      </div>
    </div>

    <template v-else>
      <!-- Summary -->
      <section
        class="grid overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1 shadow-sm md:grid-cols-[1fr_auto]"
      >
        <div class="p-5 sm:p-6">
          <p
            class="mb-1 text-xs font-semibold uppercase tracking-wide text-n-slate-10"
          >
            {{ copy.summary.title }}
          </p>
          <p class="text-sm leading-relaxed text-n-slate-11">
            {{ copy.summary.description }}
          </p>
        </div>
        <div
          class="grid grid-cols-2 border-t border-n-weak md:min-w-80 md:border-l md:border-t-0"
        >
          <div
            class="flex items-center gap-3 border-r border-n-weak bg-n-alpha-1 p-4"
          >
            <div
              class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-n-brand/10 text-n-brand"
            >
              <Icon icon="i-lucide-sparkles" class="size-5" />
            </div>
            <div class="min-w-0">
              <p class="text-xs text-n-slate-10">
                {{ copy.summary.activePrompts }}
              </p>
              <p class="text-xl font-semibold text-n-slate-12">
                {{ activePrompts.length }}
              </p>
            </div>
          </div>
          <div class="flex items-center gap-3 bg-n-alpha-1 p-4">
            <div
              class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-10"
            >
              <Icon icon="i-lucide-cpu" class="size-5" />
            </div>
            <div class="min-w-0">
              <p class="text-xs text-n-slate-10">{{ copy.summary.models }}</p>
              <p
                class="truncate text-xs font-semibold text-n-slate-12 leading-relaxed"
              >
                {{ fixedPromptModelLabel }}
              </p>
            </div>
          </div>
        </div>
      </section>

      <!-- Prompts list -->
      <section
        class="overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1 shadow-sm"
      >
        <!-- Section header -->
        <div
          class="flex flex-wrap items-start justify-between gap-4 border-b border-n-weak bg-n-alpha-1 px-5 py-5 sm:px-6"
        >
          <div>
            <h2 class="text-base font-semibold text-n-slate-12">
              {{ copy.prompts.title }}
            </h2>
            <p class="mt-0.5 text-sm text-n-slate-11">
              {{ copy.prompts.description }}
            </p>
          </div>
          <div class="flex flex-wrap gap-2">
            <Button
              :label="copy.templates.open"
              icon="i-lucide-layout-template"
              color="slate"
              variant="faded"
              size="sm"
              @click="showTemplateModal = true"
            />
            <Button
              :label="copy.prompts.new"
              icon="i-lucide-plus"
              size="sm"
              @click="openNewPromptModal"
            />
          </div>
        </div>

        <!-- Prompt search -->
        <div
          v-if="prompts.length"
          class="border-b border-n-weak bg-n-solid-1 px-4 py-3 sm:px-6"
        >
          <div class="relative max-w-xl">
            <Icon
              icon="i-lucide-search"
              class="pointer-events-none absolute left-3 top-1/2 z-10 size-4 -translate-y-1/2 text-n-slate-9"
            />
            <Input
              v-model="promptSearch"
              type="search"
              :aria-label="copy.prompts.searchLabel"
              :placeholder="copy.prompts.searchPlaceholder"
              custom-input-class="!pl-9"
            />
          </div>
        </div>

        <!-- Empty state -->
        <div
          v-if="!prompts.length"
          class="flex flex-col items-center gap-3 px-6 py-12 text-center"
        >
          <Icon icon="i-lucide-file-text" class="size-10 text-n-slate-7" />
          <p class="text-sm text-n-slate-11">{{ copy.prompts.empty }}</p>
          <Button
            class="mt-1"
            :label="copy.prompts.new"
            icon="i-lucide-plus"
            size="sm"
            @click="openNewPromptModal"
          />
        </div>

        <!-- Search empty state -->
        <div
          v-else-if="!filteredPrompts.length"
          class="flex flex-col items-center gap-3 px-6 py-12 text-center"
        >
          <Icon icon="i-lucide-search-x" class="size-10 text-n-slate-7" />
          <p class="text-sm text-n-slate-11">
            {{ copy.prompts.searchEmpty }}
          </p>
          <Button
            label="Limpar busca"
            icon="i-lucide-x"
            color="slate"
            variant="faded"
            size="sm"
            @click="promptSearch = ''"
          />
        </div>

        <!-- Cards list -->
        <div
          v-else
          class="grid gap-3 p-3 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4"
        >
          <article
            v-for="prompt in filteredPrompts"
            :key="prompt.id"
            class="group relative cursor-pointer overflow-hidden rounded-xl border border-n-weak bg-n-solid-1 p-3 transition hover:border-n-brand hover:bg-n-alpha-1 hover:shadow-md"
          >
            <button
              type="button"
              class="absolute inset-0 cursor-pointer rounded-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-n-brand"
              :aria-label="`${prompt.system_default ? copy.prompts.readMore : copy.form.editPrompt}: ${prompt.name}`"
              @click="
                prompt.system_default
                  ? openPromptDetails(prompt)
                  : editPrompt(prompt)
              "
            />
            <div class="flex h-full flex-col gap-3">
              <!-- Left: name + meta -->
              <div class="min-w-0 flex-1">
                <div class="flex flex-wrap items-center gap-2">
                  <h3 class="text-sm font-semibold text-n-slate-12">
                    {{ prompt.name }}
                  </h3>
                  <span
                    class="rounded-full border border-n-weak bg-n-alpha-1 px-2 py-0.5 text-xs text-n-slate-11"
                  >
                    {{ prompt.category_label }}
                  </span>
                  <span
                    v-if="prompt.system_default"
                    class="inline-flex items-center gap-1 rounded-full bg-n-brand/10 px-2 py-0.5 text-xs font-medium text-n-brand"
                  >
                    <span class="i-lucide-sparkles size-3" />
                    {{ copy.prompts.systemBadge }}
                  </span>
                  <span
                    class="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium"
                    :class="
                      prompt.active
                        ? 'border-n-teal-6 bg-n-teal-3 text-n-teal-10'
                        : 'border-n-weak bg-n-alpha-1 text-n-slate-10'
                    "
                  >
                    <span
                      class="h-1.5 w-1.5 rounded-full"
                      :class="prompt.active ? 'bg-n-teal-9' : 'bg-n-slate-7'"
                    />
                    {{ prompt.active ? copy.form.active : copy.form.inactive }}
                  </span>
                </div>
                <p class="mt-0.5 text-xs text-n-slate-10">
                  {{ prompt.model?.name || copy.form.fixedModel }}
                </p>
                <p
                  class="mt-2 text-sm leading-relaxed text-n-slate-11 line-clamp-2"
                >
                  {{ promptPreview(prompt) }}
                </p>
                <Button
                  v-if="hasPromptDetails(prompt)"
                  class="relative z-10 mt-1.5"
                  :label="copy.prompts.readMore"
                  icon="i-lucide-chevron-right"
                  trailing-icon
                  color="blue"
                  variant="link"
                  size="xs"
                  @click="openPromptDetails(prompt)"
                />
              </div>

              <!-- Right: actions -->
              <p
                v-if="prompt.system_default"
                class="relative z-10 mb-0 mt-auto border-t border-n-weak pt-2 text-xs leading-5 text-n-slate-10"
              >
                {{ copy.prompts.systemHint }}
              </p>
              <div
                v-else
                class="relative z-10 mt-auto flex flex-wrap items-center gap-2 border-t border-n-weak pt-2"
              >
                <Button
                  :label="
                    prompt.active
                      ? copy.prompts.deactivate
                      : copy.prompts.activate
                  "
                  :icon="prompt.active ? 'i-lucide-pause' : 'i-lucide-play'"
                  :color="prompt.active ? 'slate' : 'teal'"
                  variant="faded"
                  size="sm"
                  :is-loading="togglingPromptId === prompt.id"
                  :disabled="togglingPromptId === prompt.id"
                  @click="requestPromptToggle(prompt)"
                />
                <Button
                  :label="copy.prompts.remove"
                  icon="i-lucide-trash-2"
                  color="ruby"
                  variant="faded"
                  size="sm"
                  @click="deletePrompt(prompt)"
                />
              </div>
            </div>
          </article>
        </div>
      </section>
    </template>

    <!-- ── Prompt form modal ── -->
    <Teleport to="body">
      <div
        v-if="showPromptModal"
        class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      >
        <form
          class="flex max-h-[94vh] w-full max-w-[1400px] flex-col overflow-hidden rounded-3xl border border-n-weak bg-n-solid-1 shadow-2xl"
          @submit.prevent="savePrompt"
        >
          <!-- Header -->
          <div
            class="flex items-start justify-between gap-4 border-b border-n-weak bg-n-alpha-1 px-6 py-5"
          >
            <div class="flex items-start gap-4">
              <div
                class="flex size-11 flex-shrink-0 items-center justify-center rounded-xl bg-n-blue-3 text-n-blue-10"
              >
                <Icon icon="i-lucide-sparkles" class="size-5" />
              </div>
              <div>
                <p
                  class="text-xs font-semibold uppercase tracking-wider text-n-blue-10"
                >
                  {{ copy.builder.eyebrow }}
                </p>
                <h2 class="mt-1 text-xl font-semibold text-n-slate-12">
                  {{
                    editingPromptId ? copy.form.editPrompt : copy.prompts.new
                  }}
                </h2>
                <p class="mt-1 max-w-2xl text-sm leading-6 text-n-slate-11">
                  {{ copy.builder.description }}
                </p>
              </div>
            </div>
            <Button
              class="flex-shrink-0"
              icon="i-lucide-x"
              color="slate"
              variant="ghost"
              @click="closePromptModal"
            />
          </div>

          <!-- Body -->
          <div
            class="grid min-h-0 flex-1 overflow-y-auto xl:grid-cols-[minmax(0,1fr)_22rem]"
          >
            <div class="space-y-5 p-5 lg:p-6">
              <section
                class="rounded-2xl border border-n-weak bg-n-solid-2 p-5"
              >
                <div class="mb-5 flex items-start gap-3">
                  <span
                    class="flex size-7 flex-shrink-0 items-center justify-center rounded-full bg-n-blue-9 text-xs font-bold text-white"
                  >
                    {{ copy.builder.stepOne }}
                  </span>
                  <div>
                    <h3 class="text-sm font-semibold text-n-slate-12">
                      {{ copy.builder.identityTitle }}
                    </h3>
                    <p class="mt-1 text-xs leading-5 text-n-slate-10">
                      {{ copy.builder.identityDescription }}
                    </p>
                  </div>
                </div>
                <div class="grid gap-4 lg:grid-cols-2">
                  <Input
                    v-model="promptForm.name"
                    :label="copy.fields.name"
                    :placeholder="copy.placeholders.name"
                    required
                  />
                  <Input
                    v-model="promptForm.ai_name"
                    :label="copy.fields.aiName"
                    :placeholder="copy.placeholders.aiName"
                  />
                  <div class="flex flex-col gap-1">
                    <label class="mb-0.5 text-heading-3 text-n-slate-12">
                      {{ copy.fields.category }}
                    </label>
                    <Select
                      v-model="promptForm.category"
                      :options="categoryOptions"
                      class="w-full [&>select]:w-full"
                    />
                  </div>
                  <div class="flex flex-col gap-1.5">
                    <label class="text-sm font-medium text-n-slate-12">{{
                      copy.fields.model
                    }}</label>
                    <div
                      class="flex h-11 items-center gap-2 rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm text-n-slate-11"
                    >
                      <Icon icon="i-lucide-cpu" class="size-4 text-n-slate-9" />
                      {{ fixedPromptModelLabel }}
                    </div>
                  </div>
                </div>
              </section>

              <section
                class="rounded-2xl border border-n-weak bg-n-solid-2 p-5"
              >
                <div class="mb-5 flex items-start gap-3">
                  <span
                    class="flex size-7 flex-shrink-0 items-center justify-center rounded-full bg-n-blue-9 text-xs font-bold text-white"
                  >
                    {{ copy.builder.stepTwo }}
                  </span>
                  <div>
                    <h3 class="text-sm font-semibold text-n-slate-12">
                      {{ copy.builder.behaviorTitle }}
                    </h3>
                    <p class="mt-1 text-xs leading-5 text-n-slate-10">
                      {{ copy.builder.behaviorDescription }}
                    </p>
                  </div>
                </div>
                <TextArea
                  ref="instructionsTextareaRef"
                  v-model="promptForm.instructions"
                  :label="copy.fields.instructions"
                  :placeholder="copy.placeholders.instructions"
                  auto-height
                  min-height="10rem"
                  max-height="22rem"
                  resize
                >
                  <div
                    class="mb-1 rounded-lg border border-n-blue-6/60 bg-n-blue-2/40 p-3"
                  >
                    <div class="mb-2 flex items-center gap-2">
                      <Icon
                        icon="i-lucide-braces"
                        class="size-4 text-n-blue-10"
                      />
                      <p class="text-xs font-semibold text-n-slate-12">
                        {{ copy.fields.variables }}
                      </p>
                    </div>
                    <p class="mb-3 text-xs leading-5 text-n-slate-10">
                      {{ copy.fields.variablesDescription }}
                    </p>
                    <div class="flex flex-wrap gap-1.5">
                      <button
                        v-for="variable in promptVariables"
                        :key="variable.key"
                        type="button"
                        class="flex items-center gap-1 rounded-md border border-n-blue-6 bg-n-solid-1 px-2 py-1 text-xs text-n-blue-11 transition-colors hover:border-n-blue-8 hover:bg-n-blue-3"
                        :title="promptVariableToken(variable.key)"
                        @mousedown.prevent
                        @click="insertPromptVariable(variable.key)"
                      >
                        <Icon icon="i-lucide-plus" class="size-3" />
                        {{ variable.label }}
                      </button>
                    </div>
                  </div>
                </TextArea>
              </section>

              <section
                class="rounded-2xl border border-n-weak bg-n-solid-2 p-5"
              >
                <div class="mb-5 flex items-start gap-3">
                  <span
                    class="flex size-7 flex-shrink-0 items-center justify-center rounded-full bg-n-blue-9 text-xs font-bold text-white"
                  >
                    {{ copy.builder.stepThree }}
                  </span>
                  <div>
                    <h3 class="text-sm font-semibold text-n-slate-12">
                      {{ copy.builder.contextTitle }}
                    </h3>
                    <p class="mt-1 text-xs leading-5 text-n-slate-10">
                      {{ copy.builder.contextDescription }}
                    </p>
                  </div>
                </div>
                <TextArea
                  v-model="promptForm.user_context"
                  :label="copy.fields.context"
                  :placeholder="copy.placeholders.context"
                  auto-height
                  min-height="8rem"
                  max-height="16rem"
                  resize
                />
              </section>

              <section
                v-if="corbanActive"
                class="rounded-2xl border border-n-blue-6/60 bg-n-blue-2/30 p-5"
              >
                <div class="mb-4 flex items-start gap-3">
                  <div
                    class="flex size-9 flex-shrink-0 items-center justify-center rounded-lg bg-n-blue-3 text-n-blue-10"
                  >
                    <Icon icon="i-lucide-shield-check" class="size-5" />
                  </div>
                  <div>
                    <h3 class="text-sm font-semibold text-n-slate-12">
                      {{ copy.authorizationNotice.title }}
                    </h3>
                    <p class="mt-1 text-xs leading-5 text-n-slate-11">
                      {{ copy.authorizationNotice.description }}
                    </p>
                  </div>
                </div>
                <TextArea
                  v-model="promptForm.consultation_authorization_notice_text"
                  :label="copy.fields.authorizationNotice"
                  :placeholder="copy.placeholders.authorizationNotice"
                  :max-length="2000"
                  show-character-count
                  auto-height
                  min-height="9rem"
                  max-height="18rem"
                  resize
                />
                <div
                  class="mt-2 flex flex-col gap-1 text-xs text-n-slate-10 sm:flex-row sm:items-center sm:justify-between"
                >
                  <span>
                    {{ copy.authorizationNotice.urlNotice }}
                    {{ copy.authorizationNotice.emptyNotice }}
                  </span>
                </div>
              </section>

              <section
                class="rounded-2xl border border-n-weak bg-n-solid-2 p-5"
              >
                <div
                  class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between"
                >
                  <div class="flex items-start gap-3">
                    <div
                      class="flex size-9 flex-shrink-0 items-center justify-center rounded-lg bg-n-teal-3 text-n-teal-10"
                    >
                      <Icon icon="i-lucide-library" class="size-5" />
                    </div>
                    <div>
                      <h3 class="text-sm font-semibold text-n-slate-12">
                        {{ copy.knowledgeBase.title }}
                      </h3>
                      <p
                        class="mt-1 max-w-2xl text-xs leading-5 text-n-slate-10"
                      >
                        {{ copy.knowledgeBase.description }}
                      </p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    class="flex-shrink-0"
                    :label="copy.knowledgeBase.add"
                    icon="i-lucide-paperclip"
                    color="teal"
                    variant="faded"
                    size="sm"
                    :is-loading="uploadingKnowledgeFiles"
                    :disabled="uploadingKnowledgeFiles"
                    @click="openKnowledgeFilePicker()"
                  />
                  <input
                    ref="knowledgeFileInput"
                    type="file"
                    accept=".md,text/markdown"
                    :multiple="!replacingKnowledgeFileId"
                    class="hidden"
                    @change="handleKnowledgeFileSelection"
                  />
                </div>

                <div
                  v-if="
                    !knowledgeBaseFiles.length && !pendingKnowledgeFiles.length
                  "
                  class="mt-4 rounded-xl border border-dashed border-n-weak px-4 py-6 text-center text-xs text-n-slate-10"
                >
                  {{ copy.knowledgeBase.empty }}
                </div>

                <div
                  v-else
                  class="mt-4 divide-y divide-n-weak rounded-xl border border-n-weak"
                >
                  <div
                    v-for="file in knowledgeBaseFiles"
                    :key="file.id"
                    class="flex flex-col gap-3 p-3 sm:flex-row sm:items-center"
                  >
                    <Icon
                      icon="i-lucide-file-text"
                      class="size-5 flex-shrink-0 text-n-teal-10"
                    />
                    <div class="min-w-0 flex-1">
                      <div class="flex flex-wrap items-center gap-2">
                        <p class="truncate text-sm font-medium text-n-slate-12">
                          {{ file.name }}
                        </p>
                        <span
                          class="rounded-full bg-n-teal-3 px-2 py-0.5 text-[11px] font-medium text-n-teal-11"
                        >
                          {{ knowledgeStatusLabel(file.status) }}
                        </span>
                      </div>
                      <p class="mt-0.5 text-xs text-n-slate-9">
                        {{ formatFileSize(file.size) }}
                        {{ copy.knowledgeBase.separator }}
                        {{ copy.knowledgeBase.version }} {{ file.version }}
                        {{ copy.knowledgeBase.separator }}
                        {{ copy.knowledgeBase.updatedAt }}
                        {{ formatUpdatedAt(file.updated_at) }}
                      </p>
                    </div>
                    <div class="flex flex-shrink-0 gap-2">
                      <Button
                        type="button"
                        :label="copy.knowledgeBase.replace"
                        icon="i-lucide-refresh-cw"
                        color="slate"
                        variant="faded"
                        size="xs"
                        :disabled="uploadingKnowledgeFiles"
                        @click="openKnowledgeFilePicker(file.id)"
                      />
                      <Button
                        type="button"
                        :label="copy.knowledgeBase.remove"
                        icon="i-lucide-trash-2"
                        color="ruby"
                        variant="faded"
                        size="xs"
                        :is-loading="deletingKnowledgeFileId === file.id"
                        :disabled="deletingKnowledgeFileId === file.id"
                        @click="removeKnowledgeBaseFile(file)"
                      />
                    </div>
                  </div>

                  <div
                    v-for="(file, index) in pendingKnowledgeFiles"
                    :key="`${file.name}-${file.size}-${index}`"
                    class="flex items-center gap-3 p-3"
                  >
                    <Icon
                      icon="i-lucide-clock-3"
                      class="size-5 text-n-amber-10"
                    />
                    <div class="min-w-0 flex-1">
                      <p class="truncate text-sm font-medium text-n-slate-12">
                        {{ file.name }}
                      </p>
                      <p class="text-xs text-n-slate-9">
                        {{ formatFileSize(file.size) }}
                        {{ copy.knowledgeBase.separator }}
                        {{ copy.knowledgeBase.pending }}
                      </p>
                    </div>
                    <Button
                      type="button"
                      icon="i-lucide-x"
                      color="slate"
                      variant="ghost"
                      size="xs"
                      @click="removePendingKnowledgeFile(index)"
                    />
                  </div>
                </div>
              </section>
            </div>

            <aside
              class="border-t border-n-weak bg-n-alpha-1 p-5 xl:sticky xl:top-0 xl:self-start xl:border-l xl:border-t-0"
            >
              <div class="rounded-2xl border border-n-weak bg-n-solid-1 p-4">
                <div class="flex items-center justify-between gap-3">
                  <p class="text-sm font-semibold text-n-slate-12">
                    {{ copy.builder.checklistTitle }}
                  </p>
                  <span class="text-xs font-semibold text-n-blue-10">
                    {{ promptCompletionLabel }}
                  </span>
                </div>
                <div
                  class="mt-3 h-1.5 overflow-hidden rounded-full bg-n-alpha-2"
                >
                  <div
                    class="h-full rounded-full bg-n-blue-9 transition-all duration-300"
                    :style="{ width: `${promptCompletion}%` }"
                  />
                </div>
                <ul class="mt-4 space-y-2.5">
                  <li
                    v-for="item in promptCompletionItems"
                    :key="item.label"
                    class="flex items-center gap-2 text-xs"
                  >
                    <Icon
                      :icon="item.icon"
                      class="size-4"
                      :class="item.iconClass"
                    />
                    <span class="text-n-slate-11">{{ item.label }}</span>
                    <span
                      v-if="item.optional && !item.complete"
                      class="ml-auto text-n-slate-9"
                    >
                      {{ copy.builder.optional }}
                    </span>
                  </li>
                </ul>
              </div>

              <div
                class="mt-4 overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1"
              >
                <div class="border-b border-n-weak px-4 py-3">
                  <p class="text-sm font-semibold text-n-slate-12">
                    {{ copy.builder.previewTitle }}
                  </p>
                  <p class="mt-0.5 text-xs text-n-slate-10">
                    {{ copy.builder.previewDescription }}
                  </p>
                </div>
                <div class="p-4">
                  <div class="flex items-center gap-3">
                    <div
                      class="flex size-9 items-center justify-center rounded-full bg-n-blue-3 text-n-blue-10"
                    >
                      <Icon icon="i-lucide-bot" class="size-4" />
                    </div>
                    <div class="min-w-0">
                      <p class="truncate text-sm font-semibold text-n-slate-12">
                        {{ promptForm.ai_name || copy.fields.aiName }}
                      </p>
                      <p class="text-xs text-n-slate-9">
                        {{ selectedCategoryLabel }}
                      </p>
                    </div>
                  </div>
                  <dl
                    class="mt-4 divide-y divide-n-weak rounded-xl border border-n-weak bg-n-alpha-1 px-3"
                  >
                    <div
                      class="flex items-center justify-between gap-3 py-2.5 text-xs"
                    >
                      <dt class="text-n-slate-10">
                        {{ copy.builder.promptName }}
                      </dt>
                      <dd class="max-w-44 truncate font-medium text-n-slate-12">
                        {{ promptForm.name || copy.fields.name }}
                      </dd>
                    </div>
                    <div
                      class="flex items-center justify-between gap-3 py-2.5 text-xs"
                    >
                      <dt class="text-n-slate-10">
                        {{ copy.builder.instructionSize }}
                      </dt>
                      <dd class="font-medium text-n-slate-12">
                        {{ promptInstructionSize }}
                      </dd>
                    </div>
                    <div class="py-2.5 text-xs">
                      <dt class="mb-2 text-n-slate-10">
                        {{ copy.builder.variablesUsed }}
                      </dt>
                      <dd
                        v-if="promptVariablesUsed.length"
                        class="flex flex-wrap gap-1"
                      >
                        <span
                          v-for="variable in promptVariablesUsed"
                          :key="variable"
                          class="rounded-md bg-n-blue-3 px-1.5 py-0.5 font-mono text-[11px] text-n-blue-11"
                        >
                          {{ promptVariableToken(variable) }}
                        </span>
                      </dd>
                      <dd v-else class="text-n-slate-9">
                        {{ copy.builder.noVariables }}
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>

              <div
                class="mt-4 flex items-center justify-between gap-4 rounded-2xl border border-n-weak bg-n-solid-1 p-4"
              >
                <div class="min-w-0">
                  <p class="text-sm font-medium text-n-slate-12">
                    {{ copy.fields.active }}
                  </p>
                  <p class="text-xs text-n-slate-10">
                    {{
                      promptForm.active
                        ? copy.fields.activeDescription
                        : copy.fields.inactiveDescription
                    }}
                  </p>
                </div>
                <Button
                  type="button"
                  class="flex-shrink-0"
                  :label="
                    promptForm.active
                      ? copy.prompts.deactivate
                      : copy.prompts.activate
                  "
                  :icon="promptForm.active ? 'i-lucide-pause' : 'i-lucide-play'"
                  :color="promptForm.active ? 'teal' : 'slate'"
                  :variant="promptForm.active ? 'solid' : 'faded'"
                  size="sm"
                  @click="promptForm.active = !promptForm.active"
                />
              </div>
            </aside>
          </div>

          <!-- Footer -->
          <div
            class="flex flex-wrap justify-end gap-3 border-t border-n-weak px-6 py-4"
          >
            <Button
              :label="copy.prompts.cancel"
              color="slate"
              variant="outline"
              @click="closePromptModal"
            />
            <Button
              type="submit"
              :label="copy.prompts.save"
              :is-loading="isSavingPrompt"
              :disabled="isSavingPrompt"
            />
          </div>
        </form>
      </div>
    </Teleport>

    <!-- Prompt deletion confirmation -->
    <Teleport to="body">
      <div
        v-if="promptToDelete"
        class="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
        @click.self="promptToDelete = null"
      >
        <section
          class="w-full max-w-md rounded-2xl border border-n-weak bg-n-solid-1 p-6 shadow-2xl"
        >
          <div class="flex items-start gap-3">
            <div
              class="flex size-10 flex-shrink-0 items-center justify-center rounded-lg bg-n-ruby-3 text-n-ruby-10"
            >
              <Icon icon="i-lucide-trash-2" class="size-5" />
            </div>
            <div>
              <h2 class="text-base font-semibold text-n-slate-12">
                {{ copy.prompts.deleteTitle }}
              </h2>
              <p class="mt-1 text-sm leading-6 text-n-slate-11">
                <strong class="font-semibold text-n-slate-12">
                  {{ promptToDelete.name }}
                </strong>
                {{ copy.prompts.deleteDescription }}
              </p>
            </div>
          </div>
          <div class="mt-6 flex justify-end gap-3">
            <Button
              :label="copy.prompts.cancel"
              color="slate"
              variant="outline"
              @click="promptToDelete = null"
            />
            <Button
              :label="copy.prompts.deleteConfirm"
              color="ruby"
              :is-loading="isDeletingPrompt"
              :disabled="isDeletingPrompt"
              @click="confirmDeletePrompt"
            />
          </div>
        </section>
      </div>
    </Teleport>

    <!-- Prompt activation confirmation -->
    <Teleport to="body">
      <div
        v-if="promptToToggle"
        class="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
        @click.self="closePromptToggle"
      >
        <section
          class="w-full max-w-md rounded-2xl border border-n-weak bg-n-solid-1 p-6 shadow-2xl"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="`prompt-toggle-title-${promptToToggle.id}`"
        >
          <div class="flex items-start gap-3">
            <div
              class="flex size-10 flex-shrink-0 items-center justify-center rounded-lg"
              :class="
                promptToToggle.active
                  ? 'bg-n-amber-3 text-n-amber-10'
                  : 'bg-n-teal-3 text-n-teal-10'
              "
            >
              <Icon
                :icon="
                  promptToToggle.active ? 'i-lucide-pause' : 'i-lucide-play'
                "
                class="size-5"
              />
            </div>
            <div>
              <h2
                :id="`prompt-toggle-title-${promptToToggle.id}`"
                class="text-base font-semibold text-n-slate-12"
              >
                {{
                  promptToToggle.active
                    ? copy.prompts.deactivateTitle
                    : copy.prompts.activateTitle
                }}
              </h2>
              <p class="mt-1 text-sm leading-6 text-n-slate-11">
                <strong class="font-semibold text-n-slate-12">
                  {{ promptToToggle.name }}
                </strong>
                {{
                  promptToToggle.active
                    ? copy.prompts.deactivateDescription
                    : copy.prompts.activateDescription
                }}
              </p>
            </div>
          </div>
          <div class="mt-6 flex justify-end gap-3">
            <Button
              :label="copy.prompts.cancel"
              color="slate"
              variant="outline"
              :disabled="Boolean(togglingPromptId)"
              @click="closePromptToggle"
            />
            <Button
              :label="
                promptToToggle.active
                  ? copy.prompts.deactivateConfirm
                  : copy.prompts.activateConfirm
              "
              :color="promptToToggle.active ? 'amber' : 'teal'"
              :is-loading="togglingPromptId === promptToToggle.id"
              :disabled="Boolean(togglingPromptId)"
              @click="confirmPromptToggle"
            />
          </div>
        </section>
      </div>
    </Teleport>

    <!-- ── Templates modal ── -->
    <Teleport to="body">
      <div
        v-if="showTemplateModal"
        class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
        @click.self="showTemplateModal = false"
      >
        <section
          class="flex max-h-[88vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1 shadow-2xl"
        >
          <!-- Header -->
          <div
            class="flex items-start justify-between gap-4 border-b border-n-weak px-6 py-5"
          >
            <div>
              <h2 class="text-lg font-semibold text-n-slate-12">
                {{ copy.templates.title }}
              </h2>
              <p class="mt-0.5 text-sm text-n-slate-11">
                {{ copy.templates.description }}
              </p>
            </div>
            <Button
              class="flex-shrink-0"
              icon="i-lucide-x"
              color="slate"
              variant="ghost"
              @click="showTemplateModal = false"
            />
          </div>

          <!-- Filters -->
          <div
            class="flex flex-wrap items-center gap-3 border-b border-n-weak px-6 py-4"
          >
            <!-- Search -->
            <Input
              v-model="templateSearch"
              class="min-w-48 flex-1"
              custom-input-class="!pl-10"
              type="search"
              :placeholder="copy.templates.search"
            >
              <template #prefix>
                <Icon
                  icon="i-lucide-search"
                  class="pointer-events-none absolute left-3 top-1/2 z-10 size-4 -translate-y-1/2 text-n-slate-9"
                />
              </template>
            </Input>
            <Select
              v-model="selectedTemplateCategory"
              :options="templateCategoryOptions"
              class="w-full sm:w-56 [&>select]:w-full"
            />
          </div>

          <!-- Template cards -->
          <div class="min-h-0 flex-1 overflow-y-auto px-6 py-5">
            <div
              v-if="!filteredTemplates.length"
              class="flex flex-col items-center gap-3 py-12 text-center"
            >
              <Icon icon="i-lucide-search-x" class="size-10 text-n-slate-7" />
              <p class="text-sm text-n-slate-11">{{ copy.templates.empty }}</p>
            </div>
            <div v-else class="grid gap-4 md:grid-cols-2">
              <article
                v-for="template in filteredTemplates"
                :key="template.name"
                class="group flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4 transition-all hover:-translate-y-0.5 hover:border-n-slate-6 hover:shadow-lg"
              >
                <div class="flex-1">
                  <div
                    class="mb-2 flex flex-wrap items-start justify-between gap-2"
                  >
                    <h3 class="text-sm font-semibold text-n-slate-12">
                      {{ template.name }}
                    </h3>
                    <span
                      class="rounded-full border border-n-weak bg-n-alpha-1 px-2 py-0.5 text-xs text-n-slate-10"
                    >
                      {{ categories[template.category] || template.category }}
                    </span>
                  </div>
                  <p
                    class="text-sm leading-relaxed text-n-slate-11 line-clamp-3"
                  >
                    {{ template.description }}
                  </p>
                </div>
                <Button
                  class="mt-4 w-full"
                  :label="copy.templates.use"
                  icon="i-lucide-check"
                  color="blue"
                  variant="faded"
                  size="sm"
                  @click="useTemplate(template)"
                />
              </article>
            </div>
          </div>
        </section>
      </div>
    </Teleport>

    <!-- ── Prompt details modal ── -->
    <Teleport to="body">
      <div
        v-if="selectedPrompt"
        class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
        @click.self="closePromptDetails"
      >
        <section
          class="flex max-h-[88vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1 shadow-2xl"
        >
          <div
            class="flex items-start justify-between gap-4 border-b border-n-weak px-6 py-5"
          >
            <div>
              <p
                class="text-xs font-semibold uppercase tracking-wide text-n-brand"
              >
                {{ copy.prompts.detailsTitle }}
              </p>
              <h2 class="mt-1 text-lg font-semibold text-n-slate-12">
                {{ selectedPrompt.name }}
              </h2>
              <div class="mt-2 flex flex-wrap gap-2">
                <span
                  class="rounded-full border border-n-weak bg-n-alpha-1 px-2 py-0.5 text-xs text-n-slate-11"
                >
                  {{ selectedPrompt.category_label }}
                </span>
                <span
                  class="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium"
                  :class="
                    selectedPrompt.active
                      ? 'border-n-teal-6 bg-n-teal-3 text-n-teal-10'
                      : 'border-n-weak bg-n-alpha-1 text-n-slate-10'
                  "
                >
                  <span
                    class="h-1.5 w-1.5 rounded-full"
                    :class="
                      selectedPrompt.active ? 'bg-n-teal-9' : 'bg-n-slate-7'
                    "
                  />
                  {{
                    selectedPrompt.active
                      ? copy.form.active
                      : copy.form.inactive
                  }}
                </span>
                <span class="text-xs text-n-slate-10">
                  {{ selectedPrompt.model?.name || copy.form.fixedModel }}
                </span>
              </div>
            </div>
            <div class="flex flex-shrink-0 gap-2">
              <Button
                v-if="!selectedPrompt?.system_default"
                :label="copy.prompts.edit"
                icon="i-lucide-pencil"
                color="slate"
                variant="faded"
                size="sm"
                @click="
                  editPrompt(selectedPrompt);
                  closePromptDetails();
                "
              />
              <Button
                icon="i-lucide-x"
                color="slate"
                variant="ghost"
                @click="closePromptDetails"
              />
            </div>
          </div>

          <div class="min-h-0 flex-1 overflow-y-auto px-6 py-5 space-y-5">
            <div>
              <p
                class="mb-2 text-xs font-semibold uppercase tracking-wide text-n-slate-10"
              >
                {{ copy.fields.instructions }}
              </p>
              <div
                class="whitespace-pre-wrap rounded-xl border border-n-weak bg-n-alpha-1 p-4 font-mono text-sm leading-relaxed text-n-slate-11"
              >
                {{ selectedPrompt.instructions }}
              </div>
            </div>
            <div v-if="selectedPrompt.user_context">
              <p
                class="mb-2 text-xs font-semibold uppercase tracking-wide text-n-slate-10"
              >
                {{ copy.fields.context }}
              </p>
              <div
                class="whitespace-pre-wrap rounded-xl border border-n-weak bg-n-alpha-1 p-4 font-mono text-sm leading-relaxed text-n-slate-11"
              >
                {{ selectedPrompt.user_context }}
              </div>
            </div>
          </div>

          <div class="flex justify-end border-t border-n-weak px-6 py-4">
            <Button
              :label="copy.prompts.close"
              color="slate"
              variant="outline"
              size="sm"
              @click="closePromptDetails"
            />
          </div>
        </section>
      </div>
    </Teleport>
  </main>
</template>
